import { useCallback, useMemo, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Modal, Space, notification, Alert } from 'antd'
import { useForm, FormProvider } from 'react-hook-form'
import moment from 'moment'

import { COUNTRY } from '@qflow/constants'
import { Queries } from 'admin/lib/Api'
import { ScreenSpin } from 'admin/lib/ScreenSpin'
import { Breadcrumb, BreadcrumbArrayItem } from 'admin/lib/Breadcrumbs'
import { Layout } from 'admin/lib/Layouts'
import { Flag } from 'admin/lib/Icons/Flag'
import * as ROUTES from 'admin/routes'

import { Delivery } from './Delivery'
import { Waste } from './Waste'

import { AddInfractionButton } from 'admin/lib/ManualInfractions'
import { QuickLink } from 'admin/lib/QuickLink/QuickLink'
import { RecordDotMenuButton } from 'admin/lib/RecordDotMenu'
import { RecordHistoryButton } from 'admin/lib/RecordHistoryButton/RecordHistoryButton'
import { ShortcutsModalButton } from 'admin/lib/ShortcutsModalButton'
import { WikiLinkButton } from 'admin/lib/WikiLinkButton'

import {
  InfractionsProvider,
  recordTransformer,
  useRecordLoader,
  ModuleLabels
} from 'admin/lib/Records'
import { getFields } from 'admin/lib/Records/fields_v2'
import { ViewInternalNotesButton } from 'admin/lib/Comments/InternalNotesButton'
import { useAppInsights } from 'admin/lib/AppInsights/appinsights'

const ModuleViews = {
  delivery: Delivery,
  waste: Waste,
  default: () => `No View for Module`
}

export function RecordEdit({ contextLoader }) {
  const [pageLoadDateTime, setPageLoadDateTime] = useState()

  useEffect(() => {
    if (!pageLoadDateTime) {
      setPageLoadDateTime(new Date().toISOString())
    }
  }, [pageLoadDateTime])

  const [saving, setSaving] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const { accountId, projectId, moduleName, recordId } = useParams()
  const fields = useMemo(() => getFields(moduleName), [moduleName])

  const isImperial = contextLoader?.project?.country === COUNTRY.USA

  const recordLoader = useRecordLoader(
    accountId,
    projectId,
    moduleName,
    recordId
  )
  const transformedRecord = useMemo(
    () => recordTransformer(recordLoader.record, moduleName, isImperial),
    [isImperial, moduleName, recordLoader.record]
  )

  useEffect(() => {
    if (recordLoader.loaded) {
      setSaving(false)
    }
  }, [recordLoader])

  const startingStatus = recordLoader?.record?.status
  const recordUpdater = Queries.useRecordUpdater()

  const loaded =
    contextLoader.loaded &&
    recordLoader.loaded &&
    !recordUpdater.isLoading &&
    !saving

  const ModuleView =
    ModuleViews[moduleName.toLowerCase()] ?? ModuleViews.default

  const form = useForm({
    reValidateMode: 'all',
    mode: 'all'
  })

  const appInsights = useAppInsights()

  const user = Queries.useUserSelf()

  const submit = useCallback(
    async (record, dataView) => {
      const startTime = performance.now()
      setSaving(true)
      try {
        await recordUpdater.mutateAsync({
          accountId,
          projectId,
          moduleName,
          recordId,
          record,
          isImperial,
          timeZone: contextLoader.project.timeZone,
          dataView
        })

        // Set last-data-view in session storage
        sessionStorage.setItem('last-data-view', dataView)

        notification.success({
          message: 'Success! 💪😎🍹',
          description: 'Submitted successfully. You may close this tab now.',
          placement: 'top'
        })

        const finishTime = performance.now()
        // log Save Execution time to appInsights
        appInsights.trackMetric({
          name: 'new-admin_save_record_execution_time',
          average: finishTime - startTime,
          properties: {
            rawOrEnriched: dataView,
            module: moduleName,
            projectId: projectId,
            recordId: recordId
          }
        })

        // Telemetry:If record status is set to Failed, Escalated or Published track in appInsights
        if (
          record.status === 'Failed' ||
          record.status === 'Escalated' ||
          record.status === 'Published'
        ) {
          const properties = {
            action: `${moduleName} - ${record.status}`,
            module: moduleName,
            initialStatus: startingStatus,
            recordStatus: record.status,
            statusHasChanged: startingStatus !== record.status,
            user: user.data.displayName,
            userId: user.data.userId,
            dateUser: `${moment().format('YYYY-MM-DD')} ${
              user.data.displayName
            }`,
            projectId: projectId,
            recordId: recordId,
            templateName: record.templateName,
            estimatedAccuracy: record.estimatedAccuracy,
            autoPublishFailureReasons: record.autoPublishFailureReasons
          }

          if (moduleName === 'Delivery') {
            properties.supplierOrCarrier = record.data.supplierName
          } else if (moduleName === 'Waste') {
            properties.supplierOrCarrier = record.data.carrierName
          }

          const pageLoadEvent = {
            name: 'page-loaded',
            startTime: 0,
            timeStamp: pageLoadDateTime
          }

          // Mark submit button clicked
          performance.mark('save-clicked', {
            detail: {
              fieldName: 'Save Button'
            }
          })
          performance.measure('field-duration', {
            start: 'save-clicked',
            end: 'save-clicked',
            detail: {
              fieldName: 'Save Button'
            }
          })

          // Extract the page load, page visibility status and raw/enriched tab switches
          const matchingEntryNames = [
            'record-edit-page-load',
            'record-edit-page-status',
            'raw-enriched-tab-switch'
          ]
          const tabSwitches = performance
            .getEntriesByType('mark')
            .filter(entry => matchingEntryNames.includes(entry.name))
            .map(entry => {
              return {
                context: entry.detail.context,
                destination: entry.detail.destination,
                name: entry.name,
                startTime: Math.round(entry.startTime)
              }
            })

          // If more than 50 tab switches, split into multiple edits
          const tabSwitchesObject = {}
          if (tabSwitches.length > 50) {
            const chunkedTabSwitches = tabSwitches.reduce(
              (resultArray, item, index) => {
                const chunkIndex = Math.floor(index / 50)
                if (!resultArray[chunkIndex]) {
                  resultArray[chunkIndex] = [] // start a new chunk
                }

                resultArray[chunkIndex].push(item)

                return resultArray
              },
              []
            )

            chunkedTabSwitches.forEach((edit, index) => {
              tabSwitchesObject[`tabSwitches${index > 0 ? index : ''}`] = edit
            })
          }

          // Extract the performance entries for the record save
          const filteredPerformanceMarks = performance
            .getEntriesByType('measure')
            .filter(entry => entry.name === 'field-duration')

          const flattenedPerformanceMarks = [
            pageLoadEvent,
            ...filteredPerformanceMarks.map(entry => {
              return {
                fieldName: entry.detail.fieldName,
                name: entry.name,
                startTime: Math.round(entry.startTime),
                duration: Math.round(entry.duration),
                changed: entry.detail.changed
              }
            })
          ]

          // If more than 50 performance marks, split into multiple edits
          if (flattenedPerformanceMarks.length > 50) {
            const chunkedPerformanceMarks = flattenedPerformanceMarks.reduce(
              (resultArray, item, index) => {
                const chunkIndex = Math.floor(index / 50)
                if (!resultArray[chunkIndex]) {
                  resultArray[chunkIndex] = [] // start a new chunk
                }

                resultArray[chunkIndex].push(item)

                return resultArray
              },
              []
            )

            const editsObject = {}
            chunkedPerformanceMarks.forEach((edit, index) => {
              editsObject[`edits${index > 0 ? index : ''}`] = edit
            })

            // log the performance marks to appInsights
            if (Object.keys(tabSwitchesObject).length > 1) {
              appInsights.trackEvent({
                name: 'new-admin_saved_record',
                properties: {
                  ...editsObject,
                  ...tabSwitchesObject,
                  ...properties
                }
              })
            } else {
              appInsights.trackEvent({
                name: 'new-admin_saved_record',
                properties: {
                  ...editsObject,
                  tabSwitches: tabSwitches,
                  ...properties
                }
              })
            }
          } else {
            // log the performance marks to appInsights
            if (Object.keys(tabSwitchesObject).length > 1) {
              appInsights.trackEvent({
                name: 'new-admin_saved_record',
                properties: {
                  edits: flattenedPerformanceMarks,
                  ...tabSwitchesObject,
                  ...properties
                }
              })
            } else {
              appInsights.trackEvent({
                name: 'new-admin_saved_record',
                properties: {
                  edits: flattenedPerformanceMarks,
                  tabSwitches: tabSwitches,
                  ...properties
                }
              })
            }
          }
          appInsights.flush()

          // The assumption is that the tab will not be reused for additional records but
          // just in case...
          performance.clearMarks()
          performance.clearMeasures()
          setPageLoadDateTime(null)
        }
      } catch (error) {
        const status = error.response?.status

        if (status === 409) {
          Modal.error({
            title: 'Submit Failed',
            content:
              'The record was changed elsewhere (maybe another tab, user, or automated process?) while you were editing it and cannot be submitted.'
          })
        } else {
          const formatErrors = data => {
            let errorMessage
            if (data.title && data.errors) {
              errorMessage = (
                <>
                  <p>{data.title}</p>
                  <p>
                    Errors:
                    <br />
                    {Object.entries(data.errors).map(([k, v]) => {
                      return (
                        <>
                          {k}: {JSON.stringify(v)}
                          <br />
                        </>
                      )
                    })}
                  </p>
                </>
              )
            } else {
              errorMessage = (
                <>
                  <p>Unexpected Error</p>
                  <p>
                    Error:
                    <br />
                    {data}
                  </p>
                </>
              )
            }
            return errorMessage
          }

          Modal.error({
            title: 'Submit Failed',
            content: <>{formatErrors(error.response?.data)}</>
          })
        }
      }
    },
    [
      accountId,
      appInsights,
      contextLoader.project.timeZone,
      isImperial,
      moduleName,
      pageLoadDateTime,
      projectId,
      recordId,
      recordUpdater,
      startingStatus,
      user
    ]
  )

  const movementId = form.watch(fields.movementId.path)
  useEffect(() => {
    document.title =
      'Edit: ' + (movementId ?? ModuleLabels[moduleName.toLowerCase()] ?? '')
  }, [moduleName, movementId])

  const handleRecordHistoryButtonClick = () => {
    setShowHistory(prev => !prev)
  }

  return (
    <InfractionsProvider infractionsByPath={recordLoader.infractions}>
      <FormProvider {...form}>
        <Layout
          header={
            <>
              {contextLoader?.project?.country && (
                <Flag country={contextLoader.project.country} />
              )}

              <Breadcrumb
                items={[
                  {
                    title: (
                      <BreadcrumbArrayItem
                        loading={!contextLoader.loaded}
                        href={ROUTES.INTRAY}
                      >
                        {contextLoader.account?.name}
                      </BreadcrumbArrayItem>
                    )
                  },
                  {
                    title: (
                      <BreadcrumbArrayItem
                        loading={!contextLoader.loaded}
                        href={ROUTES.INTRAY}
                      >
                        {contextLoader.project?.name}
                      </BreadcrumbArrayItem>
                    )
                  },
                  {
                    title: (
                      <BreadcrumbArrayItem
                        loading={!contextLoader.loaded}
                        href={ROUTES.INTRAY}
                      >
                        {contextLoader.module}
                      </BreadcrumbArrayItem>
                    )
                  },
                  {
                    title: (
                      <BreadcrumbArrayItem loading={!contextLoader.loaded}>
                        {recordId}
                      </BreadcrumbArrayItem>
                    )
                  }
                ]}
              />

              <Space>
                <QuickLink
                  loaded={loaded}
                  record={recordLoader?.record}
                  accountId={accountId}
                  moduleName={moduleName}
                />
                <ViewInternalNotesButton
                  loaded={loaded}
                  record={recordLoader?.record}
                />
                <AddInfractionButton loaded={loaded} />
                <RecordHistoryButton
                  loaded={loaded}
                  hasHistory={recordLoader?.record?.history}
                  showHistory={showHistory}
                  onClick={handleRecordHistoryButtonClick}
                />
                {loaded && recordLoader?.record?.status && (
                  <RecordDotMenuButton
                    recordStatus={recordLoader?.record?.status}
                    rawOrEnriched={true}
                  />
                )}
                <ShortcutsModalButton />
                {loaded && (
                  <WikiLinkButton
                    moduleName={moduleName}
                    project={contextLoader.project}
                  />
                )}
              </Space>
            </>
          }
        >
          {loaded &&
            !recordLoader.error &&
            !contextLoader.error &&
            recordLoader.record && (
              <ModuleView
                record={transformedRecord}
                infractions={recordLoader.infractions}
                submit={submit}
                project={contextLoader.project}
                isImperial={isImperial}
                showHistory={showHistory}
                onHistoryCloseClick={handleRecordHistoryButtonClick}
              />
            )}

          {loaded && recordLoader.error && (
            <Alert
              message="Error"
              description={`Oh no! There was a problem loading this record, ${recordLoader.status}: ${recordLoader.error}`}
              type="error"
              showIcon
              style={{ marginTop: '20px' }}
            />
          )}

          {loaded && contextLoader.error && (
            <Alert
              message="Error"
              description={`There was a problem loading the context data for this record, ${contextLoader.error}`}
              type="error"
              showIcon
              style={{ marginTop: '20px' }}
            />
          )}

          {!loaded && <ScreenSpin />}
        </Layout>
      </FormProvider>
    </InfractionsProvider>
  )
}
